import React, { useState, useMemo } from "react";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { useTranslation } from "react-i18next";

import {
  SideSheet,
  FormSection,
  SpecList,
  KpiGrid,
  KpiTile,
  M3Button,
} from "../components/m3";
import styles from "../components/m3/m3.module.css";
import { Structure } from "../db_access/structure";
import { Supply_chain } from "../db_access/supply_chain";

/**
 * SecondaryOpinionSheet: Material 3 (M3) slide-over sheet for requesting a Second / Expert Opinion.
 * Strictly uses M3 components, with 100% left-aligned headings and content,
 * prominent M3 Submit and Cancel buttons, and clean gap spacing.
 */
const SecondaryOpinionSheet = ({
  open,
  onClose,
  organizationName = "Organizational Inventory",
  organizationId,
  organizationType,
  ghg = null,
  emissions = [],
  assetGWP = 0,
  totalScope1 = 0,
  totalScope2 = 0,
  totalScope3 = 0,
  onValidationSuccess,
}) => {
  const { t } = useTranslation();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionReference, setSubmissionReference] = useState(null);

  // Formatted tonnages (tCO2e)
  const gwpTons = useMemo(() => Math.round((Number(assetGWP) || 0) / 1000).toLocaleString(), [assetGWP]);
  const scope1Tons = useMemo(() => Math.round((Number(totalScope1) || 0) / 1000).toLocaleString(), [totalScope1]);
  const scope2Tons = useMemo(() => Math.round((Number(totalScope2) || 0) / 1000).toLocaleString(), [totalScope2]);
  const scope3Tons = useMemo(() => Math.round((Number(totalScope3) || 0) / 1000).toLocaleString(), [totalScope3]);

  const boundary = ghg?.boundary || "Operational Control";
  const baseYear = ghg?.base_year || "2024";

  const handleSubmit = async () => {
    if (!organizationId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (organizationType === Supply_chain.label) {
        await Supply_chain.request_validation([organizationId]);
      } else {
        await Structure.request_validation([organizationId]);
      }
      const randomRef = `SPO-${baseYear}-${Math.floor(1000 + Math.random() * 9000)}`;
      setSubmissionReference(randomRef);
      setIsSubmitted(true);
      if (onValidationSuccess) onValidationSuccess();
    } catch (e) {
      console.error("Error submitting validation request:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setSubmissionReference(null);
    onClose();
  };

  return (
    <SideSheet
      open={open}
      onClose={handleClose}
      title={isSubmitted ? t("Request Submitted") : t("Request Second Opinion")}
      subtitle={
        isSubmitted
          ? t("Review initiated for your inventory package")
          : t("Submit your GHG assessment and evidence repository for expert review")
      }
      width={580}
      footer={
        isSubmitted ? (
          <div style={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
            <M3Button variant="filled" size="md" onClick={handleClose}>
              {t("Done")}
            </M3Button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
            <M3Button
              variant="filled"
              size="md"
              icon={<SendOutlinedIcon style={{ fontSize: "16px" }} />}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("Submitting...") : t("Request Review")}
            </M3Button>
            <M3Button variant="outlined" size="md" onClick={handleClose} disabled={isSubmitting}>
              {t("Cancel")}
            </M3Button>
          </div>
        )
      }
    >
      {!isSubmitted ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "18px", textAlign: "left", paddingBottom: "12px" }}>
          {/* 1. Assessment Package Summary */}
          <FormSection
            title={t("Assessment Package Summary")}
            description={t("Active inventory data and evidence documents to be reviewed")}
            icon={<BusinessOutlinedIcon />}
          >
            <div style={{ textAlign: "left" }}>
              <SpecList
                variant="grid"
                items={[
                  { label: t("Organization"), value: organizationName },
                  { label: t("Base Year"), value: baseYear },
                  { label: t("Boundary"), value: boundary },
                  { label: t("Evidence Documents"), value: t("Repository Attached") },
                ]}
              />
            </div>

            <div style={{ marginTop: "14px", textAlign: "left" }}>
              <KpiGrid columns={4} dense>
                <KpiTile
                  label={t("Total GWP")}
                  value={gwpTons}
                  unit="tCO₂e"
                  tone="gwp"
                  size="md"
                />
                <KpiTile
                  label={t("Scope 1")}
                  value={scope1Tons}
                  unit="tCO₂e"
                  tone="scope1"
                  size="md"
                />
                <KpiTile
                  label={t("Scope 2")}
                  value={scope2Tons}
                  unit="tCO₂e"
                  tone="scope2"
                  size="md"
                />
                <KpiTile
                  label={t("Scope 3")}
                  value={scope3Tons}
                  unit="tCO₂e"
                  tone="scope3"
                  size="md"
                />
              </KpiGrid>
            </div>
          </FormSection>

          {/* 2. Confidentiality & Privacy Assurance */}
          <FormSection
            title={t("Confidentiality & Privacy Assurance")}
            description={t("Protected under enterprise data non-disclosure standards")}
            icon={<SecurityOutlinedIcon />}
          >
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                lineHeight: 1.55,
                color: "var(--m3-on-surface-variant)",
                fontFamily: "var(--m3-font)",
                textAlign: "left",
              }}
            >
              {t(
                "Your organization's emissions data, calculation boundaries, and uploaded evidence documents are transmitted securely under strict non-disclosure terms. Data is evaluated exclusively by authorized sustainability auditors for second-opinion review."
              )}
            </p>
          </FormSection>
        </div>
      ) : (
        /* Submission Success Screen - 100% Left-Aligned */
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "18px",
            alignItems: "flex-start",
            textAlign: "left",
            paddingTop: "8px",
          }}
        >
          {/* Success Banner Card */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "14px",
              width: "100%",
              padding: "16px",
              borderRadius: "var(--m3-radius-md)",
              backgroundColor: "var(--m3-success-container)",
              color: "var(--m3-on-success-container)",
              border: "1px solid rgba(101, 157, 51, 0.3)",
              textAlign: "left",
              boxSizing: "border-box",
            }}
          >
            <TaskAltOutlinedIcon
              style={{
                fontSize: "24px",
                color: "var(--m3-on-success-container)",
                flexShrink: 0,
                marginTop: "2px",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", textAlign: "left" }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "var(--m3-on-success-container)",
                  fontFamily: "var(--m3-font)",
                  textAlign: "left",
                }}
              >
                {t("Request Sent Successfully")}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  lineHeight: 1.5,
                  fontFamily: "var(--m3-font)",
                  color: "var(--m3-on-success-container)",
                  textAlign: "left",
                }}
              >
                {t(
                  "The team will review your GHG inventory assessment along with all uploaded evidence documents and get back to you."
                )}
              </p>
            </div>
          </div>

          {/* Submission Details SpecList */}
          <div style={{ width: "100%", textAlign: "left" }}>
            <FormSection
              title={t("Submission Details")}
              description={t("Recorded second opinion request package")}
              icon={<TaskAltOutlinedIcon />}
            >
              <div style={{ textAlign: "left" }}>
                <SpecList
                  variant="grid"
                  items={[
                    { label: t("Reference ID"), value: submissionReference },
                    { label: t("Status"), value: t("Under Review") },
                    { label: t("Organization"), value: organizationName },
                    { label: t("Package"), value: t("Assessment & Evidence") },
                  ]}
                />
              </div>
            </FormSection>
          </div>
        </div>
      )}
    </SideSheet>
  );
};

export default SecondaryOpinionSheet;
